/**
 * EP-160 section 2.2: what the operator API's redaction did to a record
 * inside a FlowRent support session (api/app/services/redaction/walker.rb).
 *
 * - A C3 value (ID card number, tax id, LINE id, slip images, the lease PDF,
 *   attachments) is removed and `<key>_present` says whether one exists:
 *   true or false, or the count for a list.
 * - A C2 value (a tenant's name, phone, email) is masked ("ส•••",
 *   "xxx-xxx-1234") and `<key>_masked: true` sits beside it; a dropped one
 *   (address) is removed and keeps only the marker.
 *
 * The client never sends a masked value or a removed field back: the server
 * answers 422 masked_value_in_write, and a removed list sent back empty would
 * erase the stored one.
 */

function marker(record: object | null | undefined, key: string): unknown {
  if (!record || !Object.prototype.hasOwnProperty.call(record, key)) return undefined
  return Reflect.get(record, key)
}

/** The value was removed in this session (`<key>_present` is set). */
export function removedInSession(record: object | null | undefined, key: string): boolean {
  return marker(record, `${key}_present`) !== undefined
}

/** A value exists and was removed in this session: render "[ ซ่อน ]". */
export function hiddenInSession(record: object | null | undefined, key: string): boolean {
  const present = marker(record, `${key}_present`)
  return present === true || (typeof present === 'number' && present > 0)
}

/** The value is masked (or dropped) in this session. */
export function maskedInSession(record: object | null | undefined, key: string): boolean {
  return marker(record, `${key}_masked`) === true
}

/** The field cannot be written in this session: masked, dropped or removed. */
export function lockedInSession(record: object | null | undefined, key: string): boolean {
  return maskedInSession(record, key) || removedInSession(record, key)
}
